import { createContext, useContext, useState, useEffect, useCallback, useRef, ReactNode } from "react";
import { useQueryClient } from "@tanstack/react-query";
import { supabase } from "@/integrations/supabase/client";
import type { User } from "@supabase/supabase-js";

// ─────────────────────────────────────────────────────────
// SEC-02: Secure session-scoped org storage
// - sessionStorage (tab-scoped, no cross-window leak)
// - Per-user namespaced key (no multi-account collisions)
// - Base64-obfuscated payload + 8h TTL
// - Surgical cleanup on logout (no localStorage.clear())
// ─────────────────────────────────────────────────────────
const ORG_TTL_MS = 8 * 60 * 60 * 1000; // 8 horas
const orgStorageKey = (userId: string) => `kn_org_${userId.slice(-8)}`;

interface StoredOrgPayload {
  orgId: string;
  ts: number;
}

function readStoredOrg(userId: string): string | null {
  try {
    const raw = sessionStorage.getItem(orgStorageKey(userId));
    if (!raw) return null;
    const decoded = JSON.parse(atob(raw)) as StoredOrgPayload;
    if (!decoded?.orgId || typeof decoded.ts !== "number") return null;
    if (Date.now() - decoded.ts > ORG_TTL_MS) {
      sessionStorage.removeItem(orgStorageKey(userId));
      return null;
    }
    return decoded.orgId;
  } catch {
    return null;
  }
}

function writeStoredOrg(userId: string, orgId: string): void {
  try {
    const payload: StoredOrgPayload = { orgId, ts: Date.now() };
    sessionStorage.setItem(orgStorageKey(userId), btoa(JSON.stringify(payload)));
  } catch {
    /* sessionStorage lleno o bloqueado — el estado en memoria sigue siendo la fuente de verdad */
  }
}

function clearStoredOrg(userId?: string | null): void {
  if (userId) {
    sessionStorage.removeItem(orgStorageKey(userId));
    return;
  }
  Object.keys(sessionStorage)
    .filter((k) => k.startsWith("kn_org_"))
    .forEach((k) => sessionStorage.removeItem(k));
}

// ─────────────────────────────────────────────────────────
export interface OrgMembership {
  org_id: string;
  rol: string;
  status: string;
  org_name: string;
}

interface AuthContextType {
  isAuthenticated: boolean;
  user: User | null;
  loading: boolean;
  orgId: string | null;
  userRole: string | null;
  memberships: OrgMembership[];
  switchOrg: (newOrgId: string) => void;
  refreshMemberships: () => Promise<void>;
  broadcastOrgDataChanged: () => void;
  login: (email: string, password: string) => Promise<string | null>;
  signup: (email: string, password: string) => Promise<string | null>;
  logout: () => Promise<void>;
}

const AuthContext = createContext<AuthContextType | null>(null);

export const useAuth = () => {
  const ctx = useContext(AuthContext);
  if (!ctx) throw new Error("useAuth must be used within AuthProvider");
  return ctx;
};

export const AuthProvider = ({ children }: { children: ReactNode }) => {
  const queryClient = useQueryClient();
  const [user, setUser] = useState<User | null>(null);
  const [loading, setLoading] = useState(true);
  const [memberships, setMemberships] = useState<OrgMembership[]>([]);
  const [activeOrgId, setActiveOrgId] = useState<string | null>(null);
  const [userRole, setUserRole] = useState<string | null>(null);

  // SEC-07: BroadcastChannel para sincronización multi-pestaña
  const channelRef = useRef<BroadcastChannel | null>(null);
  const tabIdRef = useRef<string>(
    typeof crypto !== "undefined" && "randomUUID" in crypto
      ? crypto.randomUUID()
      : Math.random().toString(36).slice(2)
  );
  const userRef = useRef<User | null>(null);
  useEffect(() => {
    userRef.current = user;
  }, [user]);

  const activateOrg = useCallback(
    (orgId: string, membList: OrgMembership[], userId: string) => {
      const m = membList.find((x) => x.org_id === orgId);
      if (!m) return;
      setActiveOrgId(m.org_id);
      setUserRole(m.rol);
      writeStoredOrg(userId, m.org_id);
    },
    []
  );

  const fetchOrgMemberships = useCallback(
    async (userId: string) => {
      const { data, error } = await supabase
        .from("miembros_organizacion")
        .select("org_id, rol, status, organizaciones(nombre)")
        .eq("user_id", userId);

      if (error || !data || data.length === 0) {
        setMemberships([]);
        setActiveOrgId(null);
        setUserRole(null);
        return;
      }

      const activeMemberships: OrgMembership[] = [];
      for (const row of data as any[]) {
        if (row.status === "inactive") continue;
        activeMemberships.push({
          org_id: row.org_id,
          rol: row.rol,
          status: row.status,
          org_name: row.organizaciones?.nombre ?? "Sin nombre",
        });
      }

      if (activeMemberships.length === 0) {
        setMemberships([]);
        setActiveOrgId(null);
        setUserRole(null);
        clearStoredOrg(userId);
        await supabase.auth.signOut();
        return;
      }

      setMemberships(activeMemberships);

      const savedOrgId = readStoredOrg(userId);
      const savedExists =
        savedOrgId && activeMemberships.some((m) => m.org_id === savedOrgId);

      activateOrg(
        savedExists ? savedOrgId! : activeMemberships[0].org_id,
        activeMemberships,
        userId
      );
    },
    [activateOrg]
  );

  const switchOrg = useCallback(
    (newOrgId: string) => {
      if (!user) return;
      const m = memberships.find((x) => x.org_id === newOrgId);
      if (!m) return;
      setActiveOrgId(m.org_id);
      setUserRole(m.rol);
      writeStoredOrg(user.id, m.org_id);
      // SEC-07: notificar a otras pestañas del mismo usuario
      try {
        channelRef.current?.postMessage({
          type: "ORG_CHANGED",
          userId: user.id,
          newOrgId: m.org_id,
          tabId: tabIdRef.current,
        });
      } catch {
        /* canal cerrado o no soportado — sin impacto en esta pestaña */
      }
      queryClient.clear();
    },
    [memberships, queryClient, user]
  );

  useEffect(() => {
    const { data: { subscription } } = supabase.auth.onAuthStateChange(
      (_event, session) => {
        setUser(session?.user ?? null);
        setLoading(false);
        if (session?.user) {
          setTimeout(() => fetchOrgMemberships(session.user.id), 0);
        } else {
          setMemberships([]);
          setActiveOrgId(null);
          setUserRole(null);
        }
      }
    );

    supabase.auth.getSession().then(({ data: { session } }) => {
      setUser(session?.user ?? null);
      setLoading(false);
      if (session?.user) {
        fetchOrgMemberships(session.user.id);
      }
    });

    return () => subscription.unsubscribe();
  }, [fetchOrgMemberships]);

  // SEC-07: BroadcastChannel — sincronización entre pestañas (same-origin)
  useEffect(() => {
    if (typeof BroadcastChannel === "undefined") return;

    const ch = new BroadcastChannel("kargonode_auth_channel");
    channelRef.current = ch;

    ch.onmessage = (event) => {
      const msg = event.data as
        | { type: "ORG_CHANGED"; userId: string; newOrgId: string; tabId: string }
        | { type: "ORG_DATA_CHANGED"; userId: string; tabId: string }
        | { type: "LOGOUT"; userId: string; tabId: string }
        | null;
      if (!msg || !msg.type) return;
      if (msg.tabId === tabIdRef.current) return;

      const currentUser = userRef.current;

      if (msg.type === "ORG_CHANGED") {
        if (!currentUser || currentUser.id !== msg.userId) return;
        writeStoredOrg(msg.userId, msg.newOrgId);
        queryClient.clear();
        window.location.reload();
        return;
      }

      if (msg.type === "ORG_DATA_CHANGED") {
        // Refresca metadatos de organizaciones en background sin reload
        if (!currentUser || currentUser.id !== msg.userId) return;
        fetchOrgMemberships(currentUser.id);
        queryClient.invalidateQueries({ queryKey: ["all-organizations"] });
        queryClient.invalidateQueries({ queryKey: ["all-users-global"] });
        queryClient.invalidateQueries({ queryKey: ["org-quota"] });
        return;
      }

      if (msg.type === "LOGOUT") {
        if (!currentUser || currentUser.id !== msg.userId) return;
        clearStoredOrg(msg.userId);
        queryClient.clear();
        window.location.href = "/login";
      }
    };

    return () => {
      ch.onmessage = null;
      ch.close();
      channelRef.current = null;
    };
  }, [queryClient, fetchOrgMemberships]);

  const refreshMemberships = useCallback(async () => {
    if (user?.id) await fetchOrgMemberships(user.id);
  }, [user, fetchOrgMemberships]);

  const broadcastOrgDataChanged = useCallback(() => {
    if (!user) return;
    try {
      channelRef.current?.postMessage({
        type: "ORG_DATA_CHANGED",
        userId: user.id,
        tabId: tabIdRef.current,
      });
    } catch {
      /* canal cerrado — sin impacto */
    }
  }, [user]);

  const login = async (email: string, password: string): Promise<string | null> => {
    const { error } = await supabase.auth.signInWithPassword({ email, password });
    return error ? error.message : null;
  };

  const signup = async (email: string, password: string): Promise<string | null> => {
    const { error } = await supabase.auth.signUp({ email, password });
    return error ? error.message : null;
  };

  const logout = async () => {
    const currentUserId = user?.id ?? null;
    // SEC-07: avisar a las otras pestañas antes de invalidar la sesión
    if (currentUserId) {
      try {
        channelRef.current?.postMessage({
          type: "LOGOUT",
          userId: currentUserId,
          tabId: tabIdRef.current,
        });
      } catch {
        /* canal cerrado — sin impacto */
      }
    }
    await supabase.auth.signOut();
    // Limpieza quirúrgica — preserva theme y session de Supabase
    clearStoredOrg(currentUserId);
    clearStoredOrg(); // barrido defensivo de cualquier kn_org_* residual
    queryClient.clear();
    window.location.href = "/login";
  };


  return (
    <AuthContext.Provider
      value={{
        isAuthenticated: !!user,
        user,
        loading,
        orgId: activeOrgId,
        userRole,
        memberships,
        switchOrg,
        refreshMemberships,
        broadcastOrgDataChanged,
        login,
        signup,
        logout,
      }}
    >
      {children}
    </AuthContext.Provider>
  );
};
